import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MasterAgreement,
  MontlyRateCalculationResponse
} from '../../../store/_declarations/target';
import { montlyRateCalculationApi } from '../../../store/api/financialCalculator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setSelectedItem } from '../../../store/slices/appSlice';
import { createArraysDownpayment, reduceFunc } from '../../../utils/array';
import { allInOneFormatter } from '../../../utils/formatters';
import { isVatInluded } from '../../../utils/marketSpecs';
import { getAmountWithVat } from '../../../utils/taxes';
import Button from '../../atoms/Button';
import Font from '../../atoms/Font';
import {
  ContentSection,
  Divider,
  HeaderProductTile,
  LegalNoteWrapper,
  OptionsWrapper,
  OptionTile,
  ServicePanelLeft,
  ServicePanelRight,
  ServiceTilesWrapper
} from './styles';

type UselectedProps = {
  item: MasterAgreement;
};

const Uselected: FC<UselectedProps> = ({ item }) => {
  const { t } = useTranslation();
  const { consumerPayload, widgetType } = useAppSelector((state) => state.app);
  const {
    listByVehicle,
    montlyRateCalculation,
    montlyRateCalculationPayloads,
    quotationLoading
  } = useAppSelector((state) => state.financialCalculator);
  const [itemDetails, setItemDetails] = useState<any>({
    productName: ''
  });

  const dispatch = useAppDispatch();
  const { notes } = useAppSelector((state) => state.legalNote);
  const withVat = isVatInluded(
    consumerPayload?.vehicle,
    consumerPayload?.context.customer,
    consumerPayload?.context.countryCode
  );

  const { downpaymentsWithVat, downpaymentsWithoutVat } =
    createArraysDownpayment({
      b2c: withVat,
      deposit:
        ((consumerPayload?.vehicle?.pricing?.basicPriceExclTax || 0) * 50) /
          100 || 0,
      depositWithVat:
        (getAmountWithVat(
          consumerPayload?.vehicle?.pricing?.basicPriceExclTax || 0
        ) *
          50) /
          100 || 0
    });

  useEffect(() => {
    if (
      item &&
      !montlyRateCalculationPayloads?.[item.masterAgreement] &&
      montlyRateCalculation?.[item.masterAgreement] !== 'rejected' &&
      !quotationLoading
    ) {
      const month = item.options.month.find((m) => m.isDefault)?.month || 0;
      const km = item.options.km.find((okm) => okm.isDefault)?.km || 0;
      const globalMileage = (km / 12) * month;
      const cashDeposit = downpaymentsWithoutVat.reduce(
        reduceFunc(item.downPaymentAmount)
      );
      dispatch(
        montlyRateCalculationApi({
          masterAgreement: item.masterAgreement,
          pricingProcedure: item.pricingProcedure,
          widgetSelection: {
            parameters: {
              cashDeposit,
              globalMileage,
              km,
              masterAgreement: item.masterAgreement,
              mileAllowKm: Number(item.mileAllowKm),
              mileAllowKmPerc: Number(item.mileAllowKmPerc),
              month,
              services: item.services.filter(
                (s) =>
                  s.isMandatory ||
                  s.isMandatoryCategory ||
                  s.zzRecomCategory ||
                  !s.isVisible
              )
            },
            vehicle: {
              accessory: item.accessory
            }
          }
        })
      );
    }
  }, [item, quotationLoading]);

  useEffect(() => {
    if (item && listByVehicle) {
      const parameters = {
        annualMileage: 0,
        cashDeposit: 0,
        duration: 0
      };

      const arrayValues = withVat
        ? downpaymentsWithVat
        : downpaymentsWithoutVat;

      const getCashDeposit = (paramCashDeposit?: number): number => {
        const parseToIntOrUndefined = (value: any): number =>
          value ? parseInt(value, 10) : 0;

        if (!paramCashDeposit) {
          return 0;
        }

        const countryCode = consumerPayload?.context.countryCode?.toUpperCase();

        if (countryCode === 'UK') {
          return 0;
        }

        const closestValue = downpaymentsWithoutVat.reduce(
          reduceFunc(paramCashDeposit)
        );

        const cashDeposit = paramCashDeposit
          ? arrayValues[downpaymentsWithoutVat.indexOf(closestValue)]
          : 0;
        return parseToIntOrUndefined(cashDeposit);
      };

      if (
        montlyRateCalculationPayloads &&
        montlyRateCalculationPayloads[item.masterAgreement]
      ) {
        const cd =
          montlyRateCalculationPayloads?.[item.masterAgreement]?.widgetSelection
            ?.parameters?.cashDeposit;

        parameters.annualMileage =
          montlyRateCalculationPayloads[item.masterAgreement]?.widgetSelection
            ?.parameters?.km || 0;
        parameters.cashDeposit = getCashDeposit(cd);
        parameters.duration =
          montlyRateCalculationPayloads[item.masterAgreement]?.widgetSelection
            ?.parameters?.month || 0;
      } else {
        const masterAgreement = listByVehicle.masterAgreement.find(
          (ma) => ma.masterAgreement === item.masterAgreement
        );

        const month =
          masterAgreement?.options.month.find((m) => m.isDefault)?.month || 0;
        const km =
          masterAgreement?.options.km.find((okm) => okm.isDefault)?.km || 0;
        parameters.annualMileage = km;
        parameters.cashDeposit = getCashDeposit(
          masterAgreement?.downPaymentAmount
        );
        parameters.duration = month;
      }

      let price: number | string = '-';

      if (
        montlyRateCalculation?.[item.masterAgreement] &&
        typeof montlyRateCalculation?.[item.masterAgreement] !== 'string'
      ) {
        const { regularPayment } = montlyRateCalculation?.[
          item.masterAgreement
        ] as MontlyRateCalculationResponse;

        price = allInOneFormatter(
          consumerPayload,
          regularPayment?.amountExclTax,
          t('Pricing')
        );
      }

      setItemDetails({
        parameters,
        price,
        productName: item.masterAgreementDesc
      });
    }
  }, [
    item,
    montlyRateCalculationPayloads,
    listByVehicle,
    montlyRateCalculation
  ]);

  return (
    <ServiceTilesWrapper>
      <ServicePanelLeft>
        {widgetType === 'modal' && (
          <HeaderProductTile>
            <Font
              customConfiguration={{
                color: 'black'
              }}
              data-testid="monthlyPrice"
              variant="heading4"
            >
              {itemDetails.productName}
            </Font>
            <Font
              customConfiguration={{
                color: 'black'
              }}
              data-testid="monthlyPrice"
              variant="heading4"
            >
              {itemDetails.price}
            </Font>
          </HeaderProductTile>
        )}
        <ContentSection>
          <OptionsWrapper>
            <OptionTile>
              <Font variant="bodySRegular">{t('Downpayment')}: </Font>
              <Font variant="bodyBold">
                {itemDetails?.parameters?.cashDeposit} {t('Currency')}
              </Font>
            </OptionTile>
            <OptionTile>
              <Font variant="bodySRegular">{t('Duration')}: </Font>
              <Font variant="bodyBold">
                {itemDetails?.parameters?.duration} {t('UnitMeasureMonths')}
              </Font>
            </OptionTile>
            <OptionTile>
              <Font variant="bodySRegular">{t('AnnualMilage')}:</Font>
              <Font variant="bodyBold">
                {itemDetails?.parameters?.annualMileage} {t('UnitMeasureKm')}
              </Font>
            </OptionTile>
          </OptionsWrapper>
          <Divider />
          <LegalNoteWrapper>
            <Font
              customConfiguration={{
                color: 'textBody'
              }}
              variant="bodySRegular"
            >
              {
                notes.find((n) => String(n.id) === String(item.masterAgreement))
                  ?.text
              }
            </Font>
          </LegalNoteWrapper>
        </ContentSection>
      </ServicePanelLeft>
      <ServicePanelRight>
        <Button
          customConfiguration={{ height: 43, width: '100%' }}
          onClick={() => dispatch(setSelectedItem(item.masterAgreement))}
          theme="light"
          variant="secondary"
        >
          {t('Select')}
        </Button>
      </ServicePanelRight>
    </ServiceTilesWrapper>
  );
};

export default Uselected;
